import type {
  PcfValidationReport,
  ValidationCheckResult,
} from '../types';
import type {
  ValidationExportFormat,
  ValidationExportScope,
  ValidationReportEnvelope,
} from './validationReportNormalizer';

export function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function formatMs(ms: number): string {
  return ms.toFixed(1);
}

export function sanitizeFileNameToken(value: string): string {
  return value
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function downloadFile(content: string, fileName: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function getCurrentDateStamp(): string {
  return new Date().toISOString().split('T')[0];
}

export function buildExportFileName(
  documentName: string | undefined,
  format: ValidationExportFormat,
  scope: ValidationExportScope,
  check: ValidationCheckResult | null
): string {
  const docToken = sanitizeFileNameToken(documentName || 'Report') || 'Report';
  const scopeToken =
    scope === 'current' && check
      ? `Check_${check.ValidationId}_${sanitizeFileNameToken(check.Title || 'Validation')}`
      : 'Full';
  const dateStamp = getCurrentDateStamp();
  return `PCF_Validation_${docToken}_${scopeToken}_${dateStamp}.${format}`;
}

export function applyValidationMetadata(
  report: PcfValidationReport,
  envelope?: ValidationReportEnvelope
): PcfValidationReport {
  const lastValidationDate = String(
    envelope?.lastValidationDate ??
    report.LastValidationDate ??
    report.ValidationDate ??
    ''
  ).trim();

  const capturedAtUtc = String(
    envelope?.lastValidationCapturedAtUtc ?? report.LastValidationCapturedAtUtc ?? ''
  ).trim();

  const capturedAtLocal = String(
    envelope?.lastValidationCapturedAtLocal ?? report.LastValidationCapturedAtLocal ?? ''
  ).trim();

  return {
    ...report,
    ValidationDate: report.ValidationDate || lastValidationDate,
    LastValidationDate: lastValidationDate || undefined,
    LastValidationCapturedAtUtc: capturedAtUtc || undefined,
    LastValidationCapturedAtLocal: capturedAtLocal || undefined,
  };
}

export interface ValidationReportMetadata {
  documentName?: string;
  validationDate?: string;
  lastValidationDate?: string;
  lastValidationCapturedAtUtc?: string;
  lastValidationCapturedAtLocal?: string;
  totalChecks?: number;
  totalIssues?: number;
}

export function extractValidationReportMetadata(
  report: PcfValidationReport | null | undefined
): ValidationReportMetadata {
  if (!report) {
    return {};
  }

  return {
    documentName: report.DocumentName,
    validationDate: report.ValidationDate,
    lastValidationDate: report.LastValidationDate,
    lastValidationCapturedAtUtc: report.LastValidationCapturedAtUtc,
    lastValidationCapturedAtLocal: report.LastValidationCapturedAtLocal,
    totalChecks: report.TotalChecks,
    totalIssues: report.TotalIssues,
  };
}
